import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { catchError, forkJoin, of } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { Company } from '../company/company.models';
import {
  UpgradeContractDraftPayload,
  UpgradeContractDraftResponse,
  SpecialOfferRequestResponse,
  UpgradeContractSignResponse,
} from '../models/upgrade-contract.models';
import { AuthService } from '../services/auth.service';
import { BillingService } from '../services/billing.service';
import { CompanyService } from '../services/company.service';
import { UpgradeContractModalData, UpgradeContractModalResult } from './upgrade-contract-modal.types';

@Component({
  selector: 'app-upgrade-contract-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    NzAlertModule,
    NzButtonModule,
    NzCheckboxModule,
    NzDatePickerModule,
    NzFormModule,
    NzGridModule,
    NzIconModule,
    NzInputModule,
    NzInputNumberModule,
    NzSelectModule,
    NzSpinModule,
  ],
  templateUrl: './upgrade-contract-modal.component.html',
  styleUrl: './upgrade-contract-modal.component.less'
})
export class UpgradeContractModalComponent implements OnInit, OnDestroy {
  private readonly draftControlNames = [
    'targetPlan',
    'legalName',
    'address',
    'vatId',
    'representativeName',
    'representativeRole',
    'primaryAccountEmail',
    'billingEmail',
    'requestedMaxProperties',
    'additionalUsersCount',
    'contractStartDate',
    'acceptedTerms',
  ] as const;
  form!: FormGroup;
  @ViewChild('signatureCanvas')
  set signatureCanvasRef(value: ElementRef<HTMLCanvasElement> | undefined) {
    this._signatureCanvasRef = value;
    if (value) {
      setTimeout(() => this.setupSignatureCanvas(), 0);
    }
  }

  get signatureCanvasRef(): ElementRef<HTMLCanvasElement> | undefined {
    return this._signatureCanvasRef;
  }

  loadingPrefill = true;
  submitting = false;
  previewing = false;
  downloadingPreview = false;
  signing = false;
  downloadingSignedContract = false;
  specialOfferThreshold: number | null = null;
  propertyUnitPriceNetYear: number | null = null;
  pricePerAdditionalUserNetYear: number | null = null;
  baseIncludedObjects: number | null = null;
  basePackagePriceNetYear: number | null = null;
  upgradePricingMode: 'base_package' | 'flat_custom_unit_price' = 'base_package';
  hasActiveCustomUnitPrice = false;
  upgradeSettingsCurrency = 'EUR';
  specialOfferSubmitted = false;
  specialOfferResponse: SpecialOfferRequestResponse | null = null;
  specialOfferRequestedCount: number | null = null;
  businessInquirySubmitted = false;
  businessInquirySubmitting = false;
  summary: UpgradeContractDraftResponse | null = null;
  pendingUpgrade: any = null;
  get hasPendingContract(): boolean {
    return !!this.pendingUpgrade?.contractStartDate;
  }

  get pendingContractDateLabel(): string {
    if (!this.pendingUpgrade?.contractStartDate) { return ''; }
    const date = new Date(this.pendingUpgrade.contractStartDate);
    if (Number.isNaN(date.getTime())) { return ''; }
    return date.toLocaleDateString(this.translate.currentLang === 'de' ? 'de-DE' : 'en-US', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  }

  downloadPendingContract(): void {
    if (!this.pendingUpgrade?.billingDocumentId) { return; }
    this.billingService.getMyBillingDocumentDownloadUrl(this.pendingUpgrade.billingDocumentId).subscribe({
      next: ({ url }) => window.open(url, '_blank', 'noopener'),
      error: (error) => this.message.error(error?.message || 'Download failed'),
    });
  }
  private lastPreviewObjectUrl: string | null = null;
  private lastPreviewFilename = 'Dienstleistungsvertrag_Scanafy_Vertragsvorschau.pdf';
  private _signatureCanvasRef?: ElementRef<HTMLCanvasElement>;
  private signatureContext: CanvasRenderingContext2D | null = null;
  private drawing = false;
  private signatureDirty = false;
  private resizeTimeoutId: ReturnType<typeof setTimeout> | null = null;
  signatureTouched = false;
  signedResult: UpgradeContractSignResponse | null = null;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private companyService: CompanyService,
    private billingService: BillingService,
    private translate: TranslateService,
    private message: NzMessageService,
    private modalRef: NzModalRef<UpgradeContractModalResult | null>,
    @Inject(NZ_MODAL_DATA) public data: UpgradeContractModalData
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      targetPlan: ['pro', [Validators.required]],
      legalName: ['', [Validators.required]],
      address: ['', [Validators.required]],
      vatId: [''],
      representativeName: ['', [Validators.required]],
      representativeRole: [''],
      primaryAccountEmail: ['', [Validators.required, Validators.email]],
      billingEmail: ['', [Validators.email]],
      requestedMaxProperties: [1, [Validators.required, this.requestedMaxPropertiesValidator.bind(this)]],
      additionalUsersCount: [0, [Validators.required, this.nonNegativeIntegerValidator.bind(this)]],
      contractStartDate: [new Date(), [Validators.required, this.pastContractStartDateValidator.bind(this)]],
      acceptedTerms: [false, [Validators.requiredTrue]],
      signingPlace: ['', [Validators.required, this.signingPlaceValidator.bind(this)]],
      acceptedFinalContract: [false, [Validators.requiredTrue]],
    });
    this.loadPrefillData();
  }

  ngOnDestroy(): void {
    if (this.lastPreviewObjectUrl) {
      URL.revokeObjectURL(this.lastPreviewObjectUrl);
      this.lastPreviewObjectUrl = null;
    }
    if (this.resizeTimeoutId) {
      clearTimeout(this.resizeTimeoutId);
      this.resizeTimeoutId = null;
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (!this.signatureCanvasRef) {
      return;
    }
    if (this.resizeTimeoutId) {
      clearTimeout(this.resizeTimeoutId);
    }
    this.resizeTimeoutId = setTimeout(() => {
      this.setupSignatureCanvas();
      this.resizeTimeoutId = null;
    }, 100);
  }

  get isSummaryVisible(): boolean {
    return !!this.summary;
  }

  get isSigned(): boolean {
    return this.summary?.status === 'signed' && !!this.signedResult;
  }

  get isPendingActivation(): boolean {
    return this.signedResult?.isPendingActivation === true;
  }

  get isBrandedDomainUpgrade(): boolean {
    return this.data?.source === 'branded_domain';
  }

  get businessInquirySource(): 'business_plan_inquiry' | 'branded_domain' {
    return this.isBrandedDomainUpgrade ? 'branded_domain' : 'business_plan_inquiry';
  }

  get activationDateLabel(): string {
    if (!this.signedResult?.activationDate) {
      return '';
    }
    const date = new Date(this.signedResult.activationDate);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    return date.toLocaleDateString(this.translate.currentLang === 'de' ? 'de-DE' : 'en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  get canSubmitSignature(): boolean {
    return !!this.summary?.id
      && !this.signing
      && this.signatureDirty
      && this.form.get('signingPlace')?.valid === true
      && this.form.get('acceptedFinalContract')?.value === true;
  }

  get hasSignature(): boolean {
    return this.signatureDirty;
  }

  get isExistingDraft(): boolean {
    return this.summary?.existingDraft === true;
  }

  get isReplacedDraft(): boolean {
    return this.summary?.replacedDraft === true;
  }

  get requiresSpecialOffer(): boolean {
    const requestedMaxProperties = Number(this.form?.get('requestedMaxProperties')?.value);
    return Number.isInteger(requestedMaxProperties)
      && requestedMaxProperties > 0
      && this.specialOfferThreshold !== null
      && requestedMaxProperties >= this.specialOfferThreshold;
  }

  get hasProPricingInfo(): boolean {
    return this.propertyUnitPriceNetYear !== null
      && this.pricePerAdditionalUserNetYear !== null
      && (this.isFlatCustomPricingMode || (
        this.baseIncludedObjects !== null
        && this.basePackagePriceNetYear !== null
      ));
  }

  get isFlatCustomPricingMode(): boolean {
    return this.upgradePricingMode === 'flat_custom_unit_price' || this.hasActiveCustomUnitPrice;
  }

  get showFlatCustomPricingNote(): boolean {
    return this.summary?.pricingMode === 'flat_custom_unit_price';
  }

  close(): void {
    if (this.lastPreviewObjectUrl) {
      URL.revokeObjectURL(this.lastPreviewObjectUrl);
      this.lastPreviewObjectUrl = null;
    }
    if (this.summary && this.signedResult) {
      this.modalRef.close({
        draft: this.summary,
        signed: true,
        plan: this.signedResult.companyPlan,
        billingDocumentId: this.signedResult.billingDocumentId,
        pendingActivation: this.signedResult.isPendingActivation === true,
        maxProperties: this.signedResult.maxProperties ?? null,
        seatLimit: this.signedResult.seatLimit ?? null,
        pendingPlan: this.signedResult.pendingPlan ?? null,
        pendingMaxProperties: this.signedResult.pendingMaxProperties ?? null,
        pendingSeatLimit: this.signedResult.pendingSeatLimit ?? null,
      });
      return;
    }
    this.modalRef.close(this.summary ? { draft: this.summary } : null);
  }

  goBackToForm(): void {
    this.summary = null;
    this.signedResult = null;
    this.specialOfferSubmitted = false;
    this.specialOfferResponse = null;
    this.specialOfferRequestedCount = null;
    this.businessInquirySubmitted = false;

    if (this.lastPreviewObjectUrl) {
      URL.revokeObjectURL(this.lastPreviewObjectUrl);
      this.lastPreviewObjectUrl = null;
    }

    this.lastPreviewFilename = 'Dienstleistungsvertrag_Scanafy_Vertragsvorschau.pdf';
    this.clearSignature();
    this.form.get('signingPlace')?.setValue('');
    this.form.get('acceptedFinalContract')?.setValue(false);
    this.form.get('signingPlace')?.markAsPristine();
    this.form.get('signingPlace')?.markAsUntouched();
    this.form.get('acceptedFinalContract')?.markAsPristine();
    this.form.get('acceptedFinalContract')?.markAsUntouched();
  }

  previewContract(): void {
    if (!this.summary?.id || this.previewing) {
      return;
    }

    const previewWindow = window.open('', '_blank', 'noopener');
    this.previewing = true;
    this.billingService.previewUpgradeContract(this.summary.id).subscribe({
      next: ({ blob, filename }) => {
        this.previewing = false;
        if (this.lastPreviewObjectUrl) {
          URL.revokeObjectURL(this.lastPreviewObjectUrl);
        }
        this.lastPreviewFilename = this.ensurePdfFilename(filename);
        const objectUrl = this.createPreviewObjectUrl(blob, this.lastPreviewFilename);
        this.lastPreviewObjectUrl = objectUrl;
        if (previewWindow) {
          previewWindow.location.href = objectUrl;
        } else {
          window.open(objectUrl, '_blank', 'noopener');
        }
        if (this.summary) {
          this.summary = {
            ...this.summary,
            status: 'pending_signature',
          };
        }
      },
      error: (error) => {
        this.previewing = false;
        previewWindow?.close();
        this.message.error(error?.message || this.translate.instant('UPGRADE_CONTRACT_MODAL.ERROR_PREVIEW'));
      }
    });
  }

  downloadPreviewContract(): void {
    if (!this.summary?.id || this.downloadingPreview) {
      return;
    }

    if (this.lastPreviewObjectUrl) {
      this.triggerPdfDownload(this.lastPreviewObjectUrl, this.lastPreviewFilename);
      return;
    }

    this.downloadingPreview = true;
    this.billingService.previewUpgradeContract(this.summary.id).subscribe({
      next: ({ blob, filename }) => {
        this.downloadingPreview = false;
        this.lastPreviewFilename = this.ensurePdfFilename(filename);
        const objectUrl = this.createPreviewObjectUrl(blob, this.lastPreviewFilename);
        this.lastPreviewObjectUrl = objectUrl;
        this.triggerPdfDownload(objectUrl, this.lastPreviewFilename);
        if (this.summary) {
          this.summary = {
            ...this.summary,
            status: 'pending_signature',
          };
        }
      },
      error: (error) => {
        this.downloadingPreview = false;
        this.message.error(error?.message || this.translate.instant('UPGRADE_CONTRACT_MODAL.ERROR_PREVIEW'));
      }
    });
  }

  submit(): void {
    if (this.submitting || this.loadingPrefill) {
      return;
    }

    this.markDraftControlsTouched();
    if (this.isDraftStepInvalid()) {
      return;
    }

    this.submitting = true;
    if (this.requiresSpecialOffer) {
      this.billingService.requestSpecialOffer({
        requestedMaxProperties: Number(this.form.get('requestedMaxProperties')?.value),
        source: 'freemium_upgrade',
        targetPlan: this.form.get('targetPlan')?.value,
        additionalUsersCount: Number(this.form.get('additionalUsersCount')?.value || 0),
        customer: this.buildPayload().customer,
      }).subscribe({
        next: (response) => {
          this.submitting = false;
          this.specialOfferSubmitted = true;
          this.specialOfferResponse = response;
          this.specialOfferRequestedCount = Number(this.form.get('requestedMaxProperties')?.value || 0);
          this.message.success(this.translate.instant('UPGRADE_CONTRACT_MODAL.SPECIAL_OFFER_SUCCESS'));
        },
        error: (error: any) => {
          this.submitting = false;
          if (error?.code === 'SPECIAL_OFFER_REQUIRED') {
            const threshold = Number(error?.specialOfferThreshold);
            this.specialOfferThreshold = Number.isFinite(threshold) && threshold > 0 ? threshold : this.specialOfferThreshold;
          }
          this.message.error(error?.message || this.translate.instant('UPGRADE_CONTRACT_MODAL.ERROR_SUBMIT'));
        }
      });
      return;
    }

    this.billingService.createUpgradeContractDraft(this.buildPayload()).subscribe({
      next: (response: UpgradeContractDraftResponse) => {
        this.submitting = false;
        if (this.lastPreviewObjectUrl) {
          URL.revokeObjectURL(this.lastPreviewObjectUrl);
          this.lastPreviewObjectUrl = null;
        }
        this.specialOfferSubmitted = false;
        this.specialOfferResponse = null;
        this.summary = response;
        this.signedResult = null;
        this.clearSignature();
        this.form.patchValue({
          signingPlace: '',
          acceptedFinalContract: false,
        }, { emitEvent: false });
        this.form.get('signingPlace')?.markAsPristine();
        this.form.get('signingPlace')?.markAsUntouched();
        this.form.get('acceptedFinalContract')?.markAsPristine();
        this.form.get('acceptedFinalContract')?.markAsUntouched();
        this.lastPreviewFilename = 'Dienstleistungsvertrag_Scanafy_Vertragsvorschau.pdf';
        if (response.replacedDraft) {
          this.message.info(this.translate.instant('UPGRADE_CONTRACT_MODAL.REPLACED_DRAFT_MESSAGE'));
        } else if (response.existingDraft) {
          this.message.info(this.translate.instant('UPGRADE_CONTRACT_MODAL.EXISTING_DRAFT_MESSAGE'));
        } else {
          this.message.success(this.translate.instant('UPGRADE_CONTRACT_MODAL.SUCCESS_MESSAGE'));
        }
      },
      error: (error: any) => {
        this.submitting = false;
        if (error?.code === 'SPECIAL_OFFER_REQUIRED') {
          const threshold = Number(error?.specialOfferThreshold);
          this.specialOfferThreshold = Number.isFinite(threshold) && threshold > 0 ? threshold : this.specialOfferThreshold;
        }
        this.message.error(error?.message || this.translate.instant('UPGRADE_CONTRACT_MODAL.ERROR_SUBMIT'));
      }
    });
  }

  submitSignature(): void {
    if (!this.summary?.id || this.signing) {
      return;
    }

    this.signatureTouched = true;
    this.form.get('signingPlace')?.markAsTouched();
    this.form.get('acceptedFinalContract')?.markAsTouched();

    if (!this.canSubmitSignature) {
      if (!this.signatureDirty) {
        this.message.warning(this.translate.instant('UPGRADE_CONTRACT_MODAL.SIGNATURE_REQUIRED'));
      }
      return;
    }

    const signatureBase64 = this.signatureCanvasRef?.nativeElement.toDataURL('image/png');
    if (!signatureBase64) {
      this.message.warning(this.translate.instant('UPGRADE_CONTRACT_MODAL.SIGNATURE_REQUIRED'));
      return;
    }

    this.signing = true;
    this.billingService.signUpgradeContract(this.summary.id, {
      signatureBase64,
      signingPlace: String(this.form.get('signingPlace')?.value || '').trim(),
      acceptedFinalContract: this.form.get('acceptedFinalContract')?.value === true,
    }).subscribe({
      next: (response) => {
        this.signing = false;
        this.summary = response.snapshot;
        this.signedResult = response;
        this.message.success(this.translate.instant('UPGRADE_CONTRACT_MODAL.SIGNED_SUCCESS'));
      },
      error: (error) => {
        this.signing = false;
        if (error?.code === 'INVALID_SIGNING_PLACE') {
          this.message.error(this.translate.instant('UPGRADE_CONTRACT_MODAL.VALIDATION.SIGNING_PLACE_INVALID'));
          this.form.get('signingPlace')?.markAsTouched();
          return;
        }
        this.message.error(error?.message || this.translate.instant('UPGRADE_CONTRACT_MODAL.SIGNING_FAILED'));
      }
    });
  }

  formatMoney(value: number | null | undefined): string {
    const amount = Number(value || 0);
    const lang = this.translate.currentLang || this.translate.defaultLang || 'de';
    const locale = lang.startsWith('de') ? 'de-DE' : 'en-US';
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  downloadSignedContract(): void {
    if (!this.signedResult?.billingDocumentId || this.downloadingSignedContract) {
      return;
    }

    this.downloadingSignedContract = true;
    this.billingService.getMyBillingDocumentDownloadUrl(this.signedResult.billingDocumentId).subscribe({
      next: ({ url }) => {
        this.downloadingSignedContract = false;
        window.open(url, '_blank', 'noopener');
      },
      error: (error) => {
        this.downloadingSignedContract = false;
        this.message.error(error?.message || this.translate.instant('UPGRADE_CONTRACT_MODAL.ERROR_DOWNLOAD_SIGNED'));
      }
    });
  }

  clearSignature(): void {
    const canvas = this.signatureCanvasRef?.nativeElement;
    const context = this.signatureContext;
    if (!canvas || !context) {
      this.signatureDirty = false;
      this.signatureTouched = false;
      return;
    }

    context.save();
    context.setTransform(1, 0, 0, 1, 0, 0);
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.restore();
    context.strokeStyle = '#111827';
    context.lineWidth = 2;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    this.signatureDirty = false;
    this.signatureTouched = false;
  }

  beginSignature(event: PointerEvent): void {
    const point = this.extractCanvasPoint(event);
    const canvas = this.signatureCanvasRef?.nativeElement;
    if (!point || !this.signatureContext || !canvas) {
      return;
    }
    if (event.cancelable) {
      event.preventDefault();
    }
    canvas.setPointerCapture?.(event.pointerId);
    this.signatureTouched = true;
    this.drawing = true;
    this.signatureContext.beginPath();
    this.signatureContext.moveTo(point.x, point.y);
  }

  drawSignature(event: PointerEvent): void {
    if (!this.drawing || !this.signatureContext) {
      return;
    }
    const point = this.extractCanvasPoint(event);
    if (!point) {
      return;
    }
    if (event.cancelable) {
      event.preventDefault();
    }
    this.signatureContext.lineTo(point.x, point.y);
    this.signatureContext.stroke();
    this.signatureDirty = true;
  }

  endSignature(event?: PointerEvent): void {
    if (event?.cancelable) {
      event.preventDefault();
    }
    this.drawing = false;
  }

  getFieldError(controlName: string): string | null {
    const control = this.form.get(controlName);
    if (!control || !(control.touched || control.dirty)) {
      return null;
    }

    if (controlName === 'signingPlace' && !String(control.value || '').trim()) {
      return this.translate.instant('UPGRADE_CONTRACT_MODAL.VALIDATION.SIGNING_PLACE_REQUIRED');
    }

    if (controlName === 'acceptedFinalContract' && control.value !== true) {
      return this.translate.instant('UPGRADE_CONTRACT_MODAL.VALIDATION.FINAL_CONTRACT_CONFIRMATION_REQUIRED');
    }

    if (!control.errors) {
      return null;
    }

    if (control.errors['required']) {
      return this.translate.instant(this.requiredTranslationKey(controlName));
    }

    if (control.errors['email']) {
      return this.translate.instant('UPGRADE_CONTRACT_MODAL.VALIDATION.EMAIL');
    }

    if (control.errors['pastContractStartDate']) {
      return this.translate.instant('UPGRADE_CONTRACT_MODAL.VALIDATION.CONTRACT_START_DATE_PAST');
    }

    if (control.errors['positiveInteger']) {
      return this.translate.instant('UPGRADE_CONTRACT_MODAL.VALIDATION.POSITIVE_INTEGER');
    }

    if (control.errors['minProperties']) {
      return this.translate.instant('UPGRADE_CONTRACT_MODAL.VALIDATION.MINIMUM_PROPERTIES');
    }

    if (control.errors['nonNegativeInteger']) {
      return this.translate.instant('UPGRADE_CONTRACT_MODAL.VALIDATION.NON_NEGATIVE_INTEGER');
    }

    if (control.errors['invalidSigningPlace']) {
      return this.translate.instant('UPGRADE_CONTRACT_MODAL.VALIDATION.SIGNING_PLACE_INVALID');
    }

    if (control.errors['requiredTrue']) {
      if (controlName === 'acceptedFinalContract') {
        return this.translate.instant('UPGRADE_CONTRACT_MODAL.VALIDATION.FINAL_CONTRACT_CONFIRMATION_REQUIRED');
      }
      return this.translate.instant('UPGRADE_CONTRACT_MODAL.VALIDATION.ACCEPT_TERMS');
    }

    return null;
  }

  planLabelKey(plan: string | null | undefined): string {
    return String(plan || '').toLowerCase() === 'business' ? 'PLAN.BUSINESS' : 'PLAN.PRO';
  }

  submitBusinessInquiry(): void {
    if (this.businessInquirySubmitting || this.loadingPrefill) {
      return;
    }

    this.businessInquirySubmitting = true;
    const rawValue = this.form.getRawValue();
    this.billingService.requestSpecialOffer({
      requestedMaxProperties: Number(rawValue.requestedMaxProperties || 0),
      source: this.businessInquirySource,
      additionalUsersCount: Number(rawValue.additionalUsersCount || 0),
      customer: {
        legalName: String(rawValue.legalName || '').trim(),
        address: String(rawValue.address || '').trim(),
        representativeName: String(rawValue.representativeName || '').trim(),
        representativeRole: String(rawValue.representativeRole || '').trim(),
        primaryAccountEmail: String(rawValue.primaryAccountEmail || '').trim(),
        billingEmail: String(rawValue.billingEmail || '').trim(),
      },
    }).subscribe({
      next: (response) => {
        this.businessInquirySubmitting = false;
        this.businessInquirySubmitted = true;
        this.specialOfferResponse = response;
        this.specialOfferRequestedCount = Number(rawValue.requestedMaxProperties || 0);
      },
      error: (error: any) => {
        this.businessInquirySubmitting = false;
        this.message.error(error?.message || this.translate.instant('UPGRADE_CONTRACT_MODAL.ERROR_SUBMIT'));
      }
    });
  }

  statusLabelKey(status: string | null | undefined): string {
    switch (String(status || '').toLowerCase()) {
      case 'pending_signature':
        return 'UPGRADE_CONTRACT_MODAL.STATUS.PENDING_SIGNATURE';
      case 'signed':
        return 'UPGRADE_CONTRACT_MODAL.STATUS.SIGNED';
      case 'expired':
        return 'UPGRADE_CONTRACT_MODAL.STATUS.EXPIRED';
      case 'draft':
      default:
        return 'UPGRADE_CONTRACT_MODAL.STATUS.DRAFT';
    }
  }

  private loadPrefillData(): void {
    const token = this.authService.getToken();
    const tokenPayload = token ? this.authService.decodeToken(token) : null;

    forkJoin({
      me: this.authService.getMe().pipe(catchError(() => of(null))),
      company: this.companyService.getCompanyDetails().pipe(catchError(() => of(null))),
      upgradeSettings: this.billingService.getUpgradeSettings().pipe(catchError(() => of({
        specialOfferThreshold: 100,
        propertyUnitPriceNetYear: 9.99,
        currency: 'EUR',
        pricePerAdditionalUserNetYear: 0,
        baseIncludedObjects: 10,
        basePackagePriceNetYear: 99,
        pricingMode: 'base_package',
        hasActiveCustomUnitPrice: false,
        pendingUpgrade: null,
      }))),
    }).subscribe({
      next: ({ me, company, upgradeSettings }) => {
        const threshold = Number(upgradeSettings?.specialOfferThreshold);
        this.specialOfferThreshold = Number.isFinite(threshold) && threshold > 0 ? threshold : 100;
        this.propertyUnitPriceNetYear = Number.isFinite(Number(upgradeSettings?.propertyUnitPriceNetYear))
          ? Number(upgradeSettings.propertyUnitPriceNetYear)
          : null;
        this.pricePerAdditionalUserNetYear = Number.isFinite(Number(upgradeSettings?.pricePerAdditionalUserNetYear))
          ? Number(upgradeSettings.pricePerAdditionalUserNetYear)
          : null;
        this.baseIncludedObjects = Number.isFinite(Number(upgradeSettings?.baseIncludedObjects))
          ? Number(upgradeSettings.baseIncludedObjects)
          : null;
        this.basePackagePriceNetYear = Number.isFinite(Number(upgradeSettings?.basePackagePriceNetYear))
          ? Number(upgradeSettings.basePackagePriceNetYear)
          : null;
        this.upgradePricingMode = upgradeSettings?.pricingMode === 'flat_custom_unit_price'
          ? 'flat_custom_unit_price'
          : 'base_package';
        this.hasActiveCustomUnitPrice = upgradeSettings?.hasActiveCustomUnitPrice === true;
        this.upgradeSettingsCurrency = String(upgradeSettings?.currency || 'EUR');
        this.pendingUpgrade = upgradeSettings?.pendingUpgrade || null;
        this.patchPrefillValues(me, company, tokenPayload);
        this.loadingPrefill = false;
      },
      error: () => {
        this.specialOfferThreshold = 100;
        this.propertyUnitPriceNetYear = null;
        this.pricePerAdditionalUserNetYear = null;
        this.baseIncludedObjects = null;
        this.basePackagePriceNetYear = null;
        this.upgradePricingMode = 'base_package';
        this.hasActiveCustomUnitPrice = false;
        this.upgradeSettingsCurrency = 'EUR';
        this.pendingUpgrade = null;
        this.patchPrefillValues(null, null, tokenPayload);
        this.loadingPrefill = false;
      }
    });
  }

  private patchPrefillValues(me: any, company: Company | null, tokenPayload: any): void {
    const primaryAccountEmail = me?.email || tokenPayload?.email || '';
    const legalName = company?.name || me?.company?.name || '';
    const address = company?.address || '';
    const representativeName = '';
    const billingEmail = company?.displayEmail || company?.email || primaryAccountEmail || '';
    const suggestedRequestedMaxProperties = Number(this.data?.suggestedRequestedMaxProperties);
    const companyMaxProperties = Number(company?.maxProperties);
    const requestedMaxProperties = Number.isInteger(suggestedRequestedMaxProperties) && suggestedRequestedMaxProperties > 0
      ? Math.max(suggestedRequestedMaxProperties, 10)
      : (Number.isInteger(companyMaxProperties) && companyMaxProperties > 0 ? Math.max(companyMaxProperties, 10) : 10);
    const suggestedAdditionalUsersCount = Number(this.data?.suggestedAdditionalUsersCount);
    const additionalUsersCount = Number.isInteger(suggestedAdditionalUsersCount) && suggestedAdditionalUsersCount >= 0
      ? suggestedAdditionalUsersCount
      : 0;

    this.form.patchValue({
      targetPlan: 'pro',
      legalName,
      address,
      vatId: '',
      representativeName,
      representativeRole: '',
      primaryAccountEmail,
      billingEmail,
      requestedMaxProperties,
      additionalUsersCount,
      contractStartDate: new Date(),
      acceptedTerms: false,
    });
  }

  private buildPayload(): UpgradeContractDraftPayload {
    const rawValue = this.form.getRawValue();
    return {
      targetPlan: 'pro',
      requestedMaxProperties: Number(rawValue.requestedMaxProperties),
      additionalUsersCount: Number(rawValue.additionalUsersCount || 0),
      contractStartDate: this.formatDateOnly(rawValue.contractStartDate),
      customer: {
        legalName: String(rawValue.legalName || '').trim(),
        address: String(rawValue.address || '').trim(),
        vatId: String(rawValue.vatId || '').trim(),
        representativeName: String(rawValue.representativeName || '').trim(),
        representativeRole: String(rawValue.representativeRole || '').trim(),
        primaryAccountEmail: String(rawValue.primaryAccountEmail || '').trim(),
        billingEmail: String(rawValue.billingEmail || '').trim(),
      },
      acceptance: {
        acceptedTerms: rawValue.acceptedTerms === true,
      }
    };
  }

  private formatDateOnly(value: Date | string | null): string {
    const date = value instanceof Date ? value : new Date(value || '');
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private ensurePdfFilename(filename: string): string {
    const trimmed = String(filename || '').trim();
    if (!trimmed) {
      return 'Dienstleistungsvertrag_Scanafy_Vertragsvorschau.pdf';
    }
    return trimmed.toLowerCase().endsWith('.pdf') ? trimmed : `${trimmed}.pdf`;
  }

  private triggerPdfDownload(objectUrl: string, filename: string): void {
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = this.ensurePdfFilename(filename);
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private createPreviewObjectUrl(blob: Blob, filename: string): string {
    const normalizedFilename = this.ensurePdfFilename(filename);
    try {
      const file = new File([blob], normalizedFilename, { type: 'application/pdf' });
      return URL.createObjectURL(file);
    } catch {
      return URL.createObjectURL(blob);
    }
  }

  private setupSignatureCanvas(): void {
    const canvas = this.signatureCanvasRef?.nativeElement;
    if (!canvas) {
      return;
    }

    const rect = canvas.getBoundingClientRect();
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const cssWidth = Math.max(Math.floor(rect.width || canvas.clientWidth || 520), 320);
    const cssHeight = Math.max(Math.floor(rect.height || 180), 180);
    canvas.style.width = `${cssWidth}px`;
    canvas.style.height = `${cssHeight}px`;
    canvas.width = Math.floor(cssWidth * ratio);
    canvas.height = Math.floor(cssHeight * ratio);
    const context = canvas.getContext('2d');
    if (!context) {
      return;
    }
    context.setTransform(1, 0, 0, 1, 0, 0);
    context.scale(ratio, ratio);
    this.signatureContext = context;
    this.clearSignature();
  }

  private extractCanvasPoint(event: PointerEvent): { x: number; y: number } | null {
    const canvas = this.signatureCanvasRef?.nativeElement;
    if (!canvas) {
      return null;
    }

    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  }

  private requiredTranslationKey(controlName: string): string {
    switch (controlName) {
      case 'targetPlan':
        return 'UPGRADE_CONTRACT_MODAL.VALIDATION.TARGET_PLAN_REQUIRED';
      case 'legalName':
        return 'UPGRADE_CONTRACT_MODAL.VALIDATION.LEGAL_NAME_REQUIRED';
      case 'address':
        return 'UPGRADE_CONTRACT_MODAL.VALIDATION.ADDRESS_REQUIRED';
      case 'representativeName':
        return 'UPGRADE_CONTRACT_MODAL.VALIDATION.REPRESENTATIVE_NAME_REQUIRED';
      case 'primaryAccountEmail':
        return 'UPGRADE_CONTRACT_MODAL.VALIDATION.PRIMARY_EMAIL_REQUIRED';
      case 'requestedMaxProperties':
        return 'UPGRADE_CONTRACT_MODAL.VALIDATION.REQUESTED_MAX_PROPERTIES_REQUIRED';
      case 'additionalUsersCount':
        return 'UPGRADE_CONTRACT_MODAL.VALIDATION.ADDITIONAL_USERS_REQUIRED';
      case 'contractStartDate':
        return 'UPGRADE_CONTRACT_MODAL.VALIDATION.CONTRACT_START_DATE_REQUIRED';
      case 'acceptedTerms':
        return 'UPGRADE_CONTRACT_MODAL.VALIDATION.ACCEPT_TERMS';
      case 'signingPlace':
        return 'UPGRADE_CONTRACT_MODAL.VALIDATION.SIGNING_PLACE_REQUIRED';
      case 'acceptedFinalContract':
        return 'UPGRADE_CONTRACT_MODAL.VALIDATION.FINAL_CONTRACT_CONFIRMATION_REQUIRED';
      default:
        return 'UPGRADE_CONTRACT_MODAL.VALIDATION.REQUIRED';
    }
  }

  private requestedMaxPropertiesValidator(control: AbstractControl): ValidationErrors | null {
    if (control.value === null || control.value === '' || typeof control.value === 'undefined') {
      return null;
    }

    const value = Number(control.value);
    if (!Number.isInteger(value) || value < 10) {
      return value < 10 ? { minProperties: true } : { positiveInteger: true };
    }

    return null;
  }

  disabledPastContractStartDates = (current: Date): boolean => {
    if (!(current instanceof Date) || Number.isNaN(current.getTime())) {
      return false;
    }

    const candidateDate = new Date(current.getFullYear(), current.getMonth(), current.getDate());
    const today = new Date();
    const todayAtLocalMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    return candidateDate.getTime() < todayAtLocalMidnight.getTime();
  };

  private pastContractStartDateValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (value === null || value === '' || typeof value === 'undefined') {
      return null;
    }

    return value instanceof Date && this.disabledPastContractStartDates(value)
      ? { pastContractStartDate: true }
      : null;
  }

  private positiveIntegerValidator(control: AbstractControl): ValidationErrors | null {
    if (control.value === null || control.value === '' || typeof control.value === 'undefined') {
      return null;
    }

    const value = Number(control.value);
    return Number.isInteger(value) && value > 0 ? null : { positiveInteger: true };
  }

  private nonNegativeIntegerValidator(control: AbstractControl): ValidationErrors | null {
    if (control.value === null || control.value === '' || typeof control.value === 'undefined') {
      return null;
    }

    const value = Number(control.value);
    return Number.isInteger(value) && value >= 0 ? null : { nonNegativeInteger: true };
  }

  private signingPlaceValidator(control: AbstractControl): ValidationErrors | null {
    if (control.value === null || control.value === '' || typeof control.value === 'undefined') {
      return null;
    }

    const normalized = String(control.value || '').trim();
    if (normalized.length < 2 || /\d/.test(normalized)) {
      return { invalidSigningPlace: true };
    }

    return null;
  }

  private markDraftControlsTouched(): void {
    this.draftControlNames.forEach((controlName) => {
      this.form.get(controlName)?.markAsTouched();
      this.form.get(controlName)?.markAsDirty();
    });
  }

  private isDraftStepInvalid(): boolean {
    return this.draftControlNames.some((controlName) => this.form.get(controlName)?.invalid === true);
  }
}
